/**
 * Feature Adoption — the part of Portal Analytics that reads the audit
 * truth tables instead of portal_events, so "is order editing used / is
 * Edit-in-Sage working / is the co-pilot driving real sends" is answerable
 * from the dashboard (kanban e7802b3b).
 */

import { useEffect, useState } from 'react';
import { portalAnalyticsService, type FeatureAdoptionResponse } from '../../services/portalAnalytics';
import dash from '../PortalAnalyticsDashboard.module.css';
import styles from './FeatureAdoptionPanel.module.css';
import { StackChart } from './StackChart';
import { formatMs, formatPct, formatWhen, humanize, rateTone } from './formatters';

interface FeatureAdoptionPanelProps {
  days: number;
  userId?: string;
}

const TONE_CLASS: Record<ReturnType<typeof rateTone>, string> = {
  good: styles.rateGood,
  warn: styles.rateWarn,
  bad: styles.rateBad,
  none: styles.muted,
};

function Rate({ value }: { value: number | null }) {
  return <span className={`${styles.rate} ${TONE_CLASS[rateTone(value)]}`}>{formatPct(value)}</span>;
}

function BarList({ rows, className }: { rows: { label: string; count: number }[]; className: string }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className={dash.barChart}>
      {rows.map((r) => (
        <div key={r.label} className={dash.barRow}>
          <span className={dash.barLabel} title={r.label}>{r.label}</span>
          <div className={dash.barTrack}>
            <div className={`${dash.barFill} ${className}`} style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
          <span className={dash.barCount}>{r.count}</span>
        </div>
      ))}
    </div>
  );
}

export function FeatureAdoptionPanel({ days, userId }: FeatureAdoptionPanelProps) {
  const [data, setData] = useState<FeatureAdoptionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    portalAnalyticsService
      .getFeatureAdoption(days, userId)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load feature adoption');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, userId]);

  if (loading) return <div className={styles.inlineLoading}>Loading feature adoption...</div>;
  if (error) return <div className={dash.error}>{error}</div>;
  if (!data) return null;

  const { order_editing: oe, edit_in_sage: sage, copilot: cp, kirk_drafting: kirk } = data;
  const humanEdits = oe.header_edits + oe.line_field_edits;
  const nothingAtAll =
    humanEdits + oe.lines_added + oe.lines_removed + sage.total_writes + cp.human_turns + cp.automated_turns + kirk.runs_total + kirk.human_sends === 0;

  return (
    <div className={dash.container} data-testid="feature-adoption-panel">
      {data.user_filter_applied && (
        <div className={styles.note} role="note">
          Filtered to one user. Kirk auto-draft runs and draft-send events carry no user, so the drafting
          funnel stays global.
        </div>
      )}

      {/* Headline */}
      <div className={dash.statsGrid}>
        <div className={dash.statCard}>
          <span className={dash.statValue}>{humanEdits.toLocaleString()}</span>
          <span className={dash.statLabel}>Human order edits</span>
        </div>
        <div className={dash.statCard}>
          <span className={dash.statValue}>{oe.orders_touched.toLocaleString()}</span>
          <span className={dash.statLabel}>Orders edited</span>
        </div>
        <div className={dash.statCard}>
          <span className={dash.statValue}><Rate value={sage.success_rate} /></span>
          <span className={dash.statLabel}>Edit-in-Sage success ({sage.confirmed} ok / {sage.failed} failed)</span>
        </div>
        <div className={dash.statCard}>
          <span className={dash.statValue}>{cp.human_turns.toLocaleString()}</span>
          <span className={dash.statLabel}>Co-pilot human turns ({cp.automated_turns} automated)</span>
        </div>
        <div className={dash.statCard}>
          <span className={dash.statValue}>{kirk.drafted} → {kirk.sends_from_drafts}</span>
          <span className={dash.statLabel}>Kirk drafts → sent from drafts</span>
        </div>
      </div>

      {nothingAtAll && (
        <div className={dash.emptyState}>No feature activity in this period</div>
      )}

      {/* ---------------- Order editing ---------------- */}
      <section className={styles.section} aria-labelledby="fa-order-editing">
        <div className={styles.sectionHeader}>
          <h2 id="fa-order-editing">Order editing</h2>
          <span className={styles.sectionSource}>order_record_audits · order_line_item_audits (change_source=user)</span>
        </div>
        <div className={dash.twoCol}>
          <div className={dash.chartContainer}>
            <h3 className={dash.chartTitle}>Edits per day</h3>
            <StackChart
              days={oe.daily}
              series={[
                { key: 'header_edits', label: 'header edits', className: styles.cPrimary },
                { key: 'line_edits', label: 'line edits', className: styles.cSecondary },
              ]}
              emptyText="No human order edits in this period"
              emptyClassName={dash.emptyState}
            />
            <div className={styles.legend}>
              <span>{oe.header_edits} header · {oe.line_field_edits} line-field · {oe.lines_added} lines added · {oe.lines_removed} lines removed</span>
            </div>
          </div>
          <div className={dash.chartContainer}>
            <h3 className={dash.chartTitle}>Most edited fields</h3>
            {oe.top_fields.length > 0 ? (
              <BarList rows={oe.top_fields.map((f) => ({ label: f.field_name, count: f.count }))} className={styles.cPrimary} />
            ) : (
              <div className={dash.emptyState}>No field edits yet</div>
            )}
          </div>
        </div>
        <div className={dash.chartContainer}>
          <h3 className={dash.chartTitle}>Editors ({oe.editors})</h3>
          {oe.by_user.length > 0 ? (
            <table className={dash.userTable}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Header edits</th>
                  <th>Line edits</th>
                  <th>Orders</th>
                  <th>Last edit</th>
                </tr>
              </thead>
              <tbody>
                {oe.by_user.map((u) => (
                  <tr key={u.user_id}>
                    <td>
                      <div>{u.user_name}</div>
                      <div className={styles.muted} style={{ fontSize: '0.75rem' }}>{u.user_email}</div>
                    </td>
                    <td><strong>{u.header_edits}</strong></td>
                    <td><strong>{u.line_edits}</strong></td>
                    <td>{u.orders_touched}</td>
                    <td>{formatWhen(u.last_edit_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={dash.emptyState}>Nobody edited an order in this period</div>
          )}
        </div>
      </section>

      {/* ---------------- Edit-in-Sage ---------------- */}
      <section className={styles.section} aria-labelledby="fa-edit-in-sage">
        <div className={styles.sectionHeader}>
          <h2 id="fa-edit-in-sage">Edit-in-Sage health</h2>
          <span className={styles.sectionSource}>sage_writes</span>
        </div>
        <div className={dash.twoCol}>
          <div className={dash.chartContainer}>
            <h3 className={dash.chartTitle}>Writes per day</h3>
            <StackChart
              days={sage.daily}
              series={[
                { key: 'confirmed', label: 'confirmed', className: styles.cGood },
                { key: 'failed', label: 'failed', className: styles.cBad },
                { key: 'in_flight', label: 'in flight', className: styles.cWarn },
              ]}
              emptyText="No Sage writes in this period"
              emptyClassName={dash.emptyState}
            />
            <div className={styles.legend}>
              <span>latency avg {formatMs(sage.latency_avg_ms)} · p95 {formatMs(sage.latency_p95_ms)}</span>
              {sage.by_source.map((s) => (
                <span key={s.source} className={`${dash.badge} ${dash.badgeGray}`}>{humanize(s.source)} {s.count}</span>
              ))}
            </div>
          </div>
          <div className={dash.chartContainer}>
            <h3 className={dash.chartTitle}>By write kind</h3>
            {sage.by_kind.length > 0 ? (
              <table className={dash.transitionTable}>
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Total</th>
                    <th>OK</th>
                    <th>Failed</th>
                    <th>In flight</th>
                    <th>Success</th>
                    <th>Avg latency</th>
                  </tr>
                </thead>
                <tbody>
                  {sage.by_kind.map((k) => (
                    <tr key={k.kind}>
                      <td><span className={`${dash.badge} ${dash.badgeBlue}`}>{humanize(k.kind)}</span></td>
                      <td><strong>{k.total}</strong></td>
                      <td>{k.confirmed}</td>
                      <td>{k.failed}</td>
                      <td>{k.in_flight}</td>
                      <td><Rate value={k.success_rate} /></td>
                      <td>{formatMs(k.avg_latency_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={dash.emptyState}>No Sage writes yet</div>
            )}
          </div>
        </div>
        <div className={dash.chartContainer}>
          <h3 className={dash.chartTitle}>Recent failures ({sage.failed} in period)</h3>
          {sage.recent_failures.length > 0 ? (
            <table className={dash.userTable} data-testid="sage-failures">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Kind</th>
                  <th>Order / entity</th>
                  <th>By</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {sage.recent_failures.map((f) => (
                  <tr key={f.write_id}>
                    <td>{formatWhen(f.failed_at ?? f.created_at)}</td>
                    <td>
                      <span className={`${dash.badge} ${dash.badgeRed}`}>{humanize(f.kind)}</span>
                      <div className={styles.muted} style={{ fontSize: '0.75rem' }}>{humanize(f.source)}</div>
                    </td>
                    <td>{f.sage_order_no ?? f.entity_ref ?? '—'}</td>
                    <td>{f.user_name ?? <span className={styles.muted}>system</span>}</td>
                    <td><div className={styles.errorText}>{f.error_message ?? '—'}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={dash.emptyState}>No failed Sage writes in this period</div>
          )}
        </div>
      </section>

      {/* ---------------- Co-pilot ---------------- */}
      <section className={styles.section} aria-labelledby="fa-copilot">
        <div className={styles.sectionHeader}>
          <h2 id="fa-copilot">Co-pilot chat</h2>
          <span className={styles.sectionSource}>agent_console_message (role=user) · automated = auto-draft runs + opening turn of headless agents</span>
        </div>
        <div className={dash.twoCol}>
          <div className={dash.chartContainer}>
            <h3 className={dash.chartTitle}>Turns per day</h3>
            <StackChart
              days={cp.daily}
              series={[
                { key: 'human_turns', label: 'human turns', className: styles.cPrimary },
                { key: 'automated_turns', label: 'automated turns', className: styles.cMuted },
              ]}
              emptyText="No co-pilot turns in this period"
              emptyClassName={dash.emptyState}
            />
            <div className={styles.legend}>
              <span>{cp.conversations} human conversations · {cp.active_users} active users</span>
            </div>
          </div>
          <div className={dash.chartContainer}>
            <h3 className={dash.chartTitle}>By profile</h3>
            {cp.by_profile.length > 0 ? (
              <table className={dash.transitionTable}>
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Human turns</th>
                    <th>Automated</th>
                    <th>Conversations</th>
                    <th>Users</th>
                  </tr>
                </thead>
                <tbody>
                  {cp.by_profile.map((p) => (
                    <tr key={p.profile_id}>
                      <td>
                        <span className={`${dash.badge} ${dash.badgePurple}`}>{p.profile_id}</span>
                        {p.kind !== 'interactive' && (
                          <span
                            className={`${dash.badge} ${dash.badgeGray}`}
                            style={{ marginLeft: 4 }}
                            title={
                              p.kind === 'headless'
                                ? 'Scheduled or pipeline agent: its opening prompt is automation; later turns are people continuing the thread'
                                : 'No current agent answers to this id (retired profile)'
                            }
                          >
                            {p.kind}
                          </span>
                        )}
                      </td>
                      <td><strong>{p.human_turns}</strong></td>
                      <td className={styles.muted}>{p.automated_turns}</td>
                      <td>{p.conversations}</td>
                      <td>{p.users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={dash.emptyState}>No co-pilot activity yet</div>
            )}
          </div>
        </div>
        <div className={dash.chartContainer}>
          <h3 className={dash.chartTitle}>Co-pilot users ({cp.active_users})</h3>
          {cp.by_user.length > 0 ? (
            <table className={dash.userTable}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Human turns</th>
                  <th>Conversations</th>
                  <th>Profiles</th>
                  <th>Last active</th>
                </tr>
              </thead>
              <tbody>
                {cp.by_user.map((u) => (
                  <tr key={u.user_id}>
                    <td>
                      <div>{u.user_name}</div>
                      <div className={styles.muted} style={{ fontSize: '0.75rem' }}>{u.user_email}</div>
                    </td>
                    <td><strong>{u.human_turns}</strong></td>
                    <td>{u.conversations}</td>
                    <td>
                      {u.profiles.map((p) => (
                        <span key={p} className={`${dash.badge} ${dash.badgePurple}`} style={{ marginRight: 4 }}>{p}</span>
                      ))}
                    </td>
                    <td>{formatWhen(u.last_active_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={dash.emptyState}>No human co-pilot turns in this period</div>
          )}
        </div>
      </section>

      {/* ---------------- Kirk drafting funnel ---------------- */}
      <section className={styles.section} aria-labelledby="fa-kirk">
        <div className={styles.sectionHeader}>
          <h2 id="fa-kirk">Kirk auto-drafting → human sends</h2>
          <span className={styles.sectionSource}>kirk_auto_draft_runs · kirk_draft_send_events · sent_emails</span>
        </div>
        <div className={dash.chartContainer}>
          <div className={styles.funnel} data-testid="kirk-funnel">
            <div className={styles.funnelStep}>
              <span className={styles.funnelValue}>{kirk.runs_total}</span>
              <span className={styles.funnelLabel}>Auto-draft runs</span>
              <span className={styles.funnelSub}>{kirk.skipped} skipped · {kirk.failed} failed · {kirk.open} open</span>
            </div>
            <div className={styles.funnelStep}>
              <span className={styles.funnelValue}>{kirk.drafted}</span>
              <span className={styles.funnelLabel}>Drafts proposed</span>
              <span className={styles.funnelSub}>draft rate <Rate value={kirk.draft_rate} /></span>
            </div>
            <div className={styles.funnelStep}>
              <span className={styles.funnelValue}>{kirk.sends_from_drafts}</span>
              <span className={styles.funnelLabel}>Sent from a draft</span>
              <span className={styles.funnelSub}>
                {kirk.avg_similarity == null
                  ? 'similarity —'
                  : `avg similarity ${formatPct(kirk.avg_similarity)} · ${kirk.sent_verbatim} verbatim`}
              </span>
            </div>
            <div className={styles.funnelStep}>
              <span className={styles.funnelValue}>{kirk.human_sends}</span>
              <span className={styles.funnelLabel}>Human sends (all)</span>
              <span className={styles.funnelSub}>sent_emails status=sent</span>
            </div>
          </div>
        </div>
        <div className={dash.chartContainer}>
          <h3 className={dash.chartTitle}>Drafting per day</h3>
          <StackChart
            days={kirk.daily}
            series={[
              { key: 'drafted', label: 'drafts proposed', className: styles.cMuted },
              { key: 'failed', label: 'draft failures', className: styles.cBad },
              { key: 'sends_from_drafts', label: 'sent from draft', className: styles.cGood },
              { key: 'human_sends', label: 'human sends', className: styles.cPrimary },
            ]}
            emptyText="No drafting or sending in this period"
            emptyClassName={dash.emptyState}
          />
        </div>
      </section>
    </div>
  );
}
